import {
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  AMBIENT_SCRIBE_STATUS_RETRYING
} from "features/medical-scribe/medical-scribe-constants";
import {
  type MicrophoneAssessment,
  MicrophoneVerificationStatus
} from "features/medical-scribe/microphone.utils";

const BAD_STATUSES = new Set<number>([
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_RETRYING,
  AMBIENT_SCRIBE_STATUS_CANCELLED
]);

const MIC_PROVEN_STATUSES = new Set<number>([
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING
]);

type AmbientScribeListItem = {
  status?: number;
  created_at?: string | null;
  microphone_data?: { device_id: string } | null;
};

export function computeShouldShowMicUnverifiedPrompt(params: {
  isEnabled: boolean;
  isLoading: boolean;
  assessment: MicrophoneAssessment | null;
  staffId: number | undefined;
  lastScribes: AmbientScribeListItem[];
  micSetupCompletedAt?: string | null;
  selectedMicDeviceId?: string;
}): boolean {
  const {
    isEnabled,
    isLoading,
    assessment,
    staffId,
    lastScribes,
    micSetupCompletedAt
  } = params;

  if (!isEnabled || isLoading || !staffId) {
    return false;
  }

  const isFirstTime = lastScribes.length === 0;
  const lastScribeHasBadStatus = Boolean(
    lastScribes[0]?.status !== undefined &&
      BAD_STATUSES.has(lastScribes[0].status as number)
  );

  // A failed/retrying/cancelled scribe is a strong signal that the microphone
  // may have had an issue. Prompt for re-verification regardless of whether the
  // mic was previously verified (canRecordImmediately bypassed this entirely).
  //
  // Exception: if the user completed the mic setup AFTER the failed scribe was
  // created, they have already re-tested their mic — skip the prompt.
  // micSetupCompletedAt is written to localStorage synchronously when the user
  // clicks "Done" in the mic setup modal, so there is no API race condition.
  //
  // When created_at is absent from the API response we cannot do an exact
  // timestamp comparison, so we fall back to trusting micSetupCompletedAt alone.
  // This is safe because micSetupCompletedAt is cleared after each recording
  // session ends, so stale setups from a previous day cannot bypass the check.
  if (lastScribeHasBadStatus) {
    if (micSetupCompletedAt) {
      const failedScribeCreatedAt = lastScribes[0]?.created_at;
      // If created_at is available, verify that setup happened after the failure.
      // If created_at is absent, trust micSetupCompletedAt unconditionally.
      const setupAfterFailure =
        !failedScribeCreatedAt ||
        new Date(micSetupCompletedAt) > new Date(failedScribeCreatedAt);
      if (setupAfterFailure) {
        return false;
      }
    }
    return true;
  }

  // Last scribe reached Completed or Paused — mic demonstrably worked in that session.
  if (
    lastScribes[0]?.status !== undefined &&
    MIC_PROVEN_STATUSES.has(lastScribes[0].status)
  ) {
    return false;
  }

  // For first-time users, trust micSetupCompletedAt if it is set — same safety
  // guarantee as the bad-scribe case: it is cleared when a recording session ends,
  // so a stale setup from a previous session cannot suppress the check.
  // This covers two cases: (1) the async performSave race where saveVerifiedMicrophone
  // hasn't written to localStorage yet when "Capture Visit" is clicked, and (2) any
  // other failure in performSave (device not found, permissions revoked, etc.).
  if (isFirstTime && micSetupCompletedAt) {
    return false;
  }

  return (
    !assessment ||
    !assessment.canRecordImmediately ||
    (assessment.verification === MicrophoneVerificationStatus.Unverified &&
      isFirstTime)
  );
}
